// Utility functions for standardizing option symbols across different broker formats
//
// STORAGE FORMAT: OCC (Options Clearing Corporation) format
// e.g., "AVXL250919P00009000" (AVXL Sep 19 2025 $9 Put)
//
// DISPLAY FORMAT: Human-readable format for UI
// e.g., "AVXL 2025-09-19 9.00 P"
//
// All option symbols should be STORED in OCC format for:
// - Industry standard compatibility
// - Simple string-based deduplication
// - Cross-broker matching
//
// Display formatting should be applied in the UI/API response layer.

import { getCompanyMappingVersion, getCompanyTicker } from '../config/symbols/index.js';
import { createBoundedMemo } from './boundedMemo.js';

/**
 * Parse an option symbol and extract its components
 * Handles both Schwab format: "SYMBOL MM/DD/YYYY STRIKE C/P"
 * and Vanguard format: "SYMBOL YYMMDD C/P STRIKE"
 */
export interface OptionSymbolParts {
  underlying: string;
  expirationDate: string; // YYYY-MM-DD format
  strike: string;
  type: 'C' | 'P'; // Call or Put
}

/**
 * An option strike as a string: two decimals, or three when the thousandths
 * digit is non-zero (OPT-08). OCC strikes carry three decimals (adjusted
 * contracts after a 3-for-2 split or a merger, e.g. $33.333); rounding them
 * to cents rewrote the symbol on every parse/format round trip. Every
 * two-decimal strike formats exactly as before ("615.00").
 */
export function formatOptionStrike(strike: number): string {
  const thousandths = Math.round(strike * 1000);
  return (thousandths / 1000).toFixed(thousandths % 10 !== 0 ? 3 : 2);
}

/**
 * Detect if a symbol is an option symbol
 */
export function isOptionSymbol(symbol: string): boolean {
  if (!symbol) return false;
  // Look for patterns that indicate options
  // Schwab: has date format MM/DD/YYYY
  // Vanguard: has YYMMDD followed by C or P and a number
  // Standardized: has YYYY-MM-DD followed by strike and C/P
  // OCC: ends with YYMMDD + C/P + 8-digit strike (e.g., QQQ260106P00615000)
  const hasSchwabDate = /\d{1,2}\/\d{1,2}\/\d{4}/.test(symbol);
  const hasVanguardPattern = /\d{6}\s+[CP]\s+[\d.]+/.test(symbol);
  const hasStandardizedPattern = /\d{4}-\d{2}-\d{2}\s+[\d.]+\s+[CP]$/.test(symbol);
  // Whitespace removed first, as parseOCCOption does, so a SnapTrade-padded
  // root ("VTI   260116P00330000") is detected as well as parsed (OPT-08).
  const hasOCCPattern = /^[A-Z][A-Z0-9.]*\d{6}[CP]\d{8}$/i.test(symbol.replace(/\s+/g, ''));
  // Vanguard dot-separated: SYMBOL.YYMMDD.C/P.STRIKE (e.g. "AMD.260618.C.240.00")
  const hasDottedVanguardPattern = /\.\d{6}\.[CP]\.\d+(?:\.\d+)?$/i.test(symbol.trim());
  return hasSchwabDate || hasVanguardPattern || hasStandardizedPattern || hasOCCPattern || hasDottedVanguardPattern;
}

/**
 * Parse Schwab option symbol format: "SYMBOL MM/DD/YYYY STRIKE C/P"
 * Example: "AVXL 09/19/2025 9.00 P"
 */
function parseSchwabOption(symbol: string): OptionSymbolParts | null {
  // Pattern: SYMBOL MM/DD/YYYY STRIKE C/P
  // Symbol can include letters, numbers, dots (e.g., BRK.B)
  const pattern = /^([A-Z][A-Z0-9.]*)\s+(\d{1,2}\/\d{1,2}\/\d{4})\s+([\d.]+)\s+([CP])$/i;
  const match = symbol.trim().toUpperCase().match(pattern);
  
  if (match) {
    // Every group is required by the pattern, so these are always present.
    const [, underlying, date, strike, type] = match;
    if (!underlying || !date || !strike || !type) return null;
    // Convert MM/DD/YYYY to YYYY-MM-DD (the pattern guarantees three parts)
    const [month = '', day = '', yyyy] = date.split('/');
    const mm = month.padStart(2, '0');
    const dd = day.padStart(2, '0');
    const isoDate = `${yyyy}-${mm}-${dd}`;
    
    return {
      underlying,
      expirationDate: isoDate,
      strike,
      type: type as 'C' | 'P'
    };
  }
  
  return null;
}

/**
 * Parse Vanguard option symbol format: "SYMBOL YYMMDD C/P STRIKE"
 * Example: "AVXL 250919 P 9.00"
 */
function parseVanguardOption(symbol: string): OptionSymbolParts | null {
  // Pattern: SYMBOL YYMMDD C/P STRIKE
  // Symbol can include letters, numbers, dots (e.g., BRK.B)
  const pattern = /^([A-Z][A-Z0-9.]*)\s+(\d{6})\s+([CP])\s+([\d.]+)$/i;
  const match = symbol.trim().toUpperCase().match(pattern);
  
  if (match) {
    // Every group is required by the pattern, so these are always present.
    const [, underlying, yymmdd, type, strike] = match;
    if (!underlying || !yymmdd || !type || !strike) return null;
    const yy = yymmdd.substring(0, 2);
    const mm = yymmdd.substring(2, 4);
    const dd = yymmdd.substring(4, 6);
    
    // Convert YY to full year (assume 20YY for years 00-99)
    const fullYear = `20${yy}`;
    const expirationDate = `${fullYear}-${mm}-${dd}`;
    
    return {
      underlying,
      expirationDate: expirationDate,
      strike,
      type: type as 'C' | 'P'
    };
  }
  
  return null;
}

/**
 * Parse Vanguard dot-separated option format: "SYMBOL.YYMMDD.C/P.STRIKE"
 * Example: "AMD.260618.C.240.00" -> AMD, Jun 18 2026, Call, $240.00
 *
 * SnapTrade surfaces some Vanguard option positions with this dotted symbol
 * (and no option metadata), which otherwise fails every other parser and lands
 * as an unclassified, non-OCC holding that won't dedupe against the
 * transaction-inferred OCC holding.
 */
function parseDottedVanguardOption(symbol: string): OptionSymbolParts | null {
  // Underlying may itself contain a dot (e.g. BRK.B); the trailing
  // .YYMMDD.[CP].STRIKE anchor disambiguates via backtracking.
  const pattern = /^([A-Z][A-Z0-9.]*)\.(\d{6})\.([CP])\.(\d+(?:\.\d+)?)$/i;
  const match = symbol.trim().toUpperCase().match(pattern);

  if (match) {
    // Every group is required by the pattern, so these are always present.
    const [, underlying, yymmdd, type, strike] = match;
    if (!underlying || !yymmdd || !type || !strike) return null;
    const yy = yymmdd.substring(0, 2);
    const mm = yymmdd.substring(2, 4);
    const dd = yymmdd.substring(4, 6);

    return {
      underlying,
      expirationDate: `20${yy}-${mm}-${dd}`,
      strike,
      type: type as 'C' | 'P',
    };
  }

  return null;
}

/**
 * Parse OCC (Options Clearing Corporation) format: "ROOTSYMBOLYYMMDDCSTRIKE*1000"
 * Example: "QQQ260106P00615000" -> QQQ, Jan 6 2026, Put, $615.00
 * Example: "HPE260123C00026000" -> HPE, Jan 23 2026, Call, $26.00
 *
 * The format is: ROOT (variable) + YYMMDD (6) + C/P (1) + STRIKE*1000 (8)
 *
 * Note: Also handles SnapTrade-style padded symbols like "VTI   260116P00330000"
 * by removing spaces before parsing.
 */
function parseOCCOption(symbol: string): OptionSymbolParts | null {
  if (!symbol) return null;

  // Remove all spaces (handles padded root symbols like "VTI   260116P00330000")
  const trimmed = symbol.replace(/\s+/g, '').toUpperCase();

  // OCC symbols are typically 15-21 characters
  // Minimum: 1 char root + 6 date + 1 type + 8 strike = 16
  if (trimmed.length < 16) return null;

  // The strike is always the last 8 characters
  // The type (C/P) is always the character before the strike
  // The date (YYMMDD) is always the 6 characters before the type
  // The root is everything before the date

  const strikeStr = trimmed.slice(-8);
  const typeChar = trimmed.slice(-9, -8);
  const dateStr = trimmed.slice(-15, -9);
  const root = trimmed.slice(0, -15);

  // Validate components
  if (!/^\d{8}$/.test(strikeStr)) return null;
  if (typeChar !== 'C' && typeChar !== 'P') return null;
  if (!/^\d{6}$/.test(dateStr)) return null;
  if (!/^[A-Z][A-Z0-9.]*$/.test(root)) return null;

  // Parse date: YYMMDD
  const yy = dateStr.substring(0, 2);
  const mm = dateStr.substring(2, 4);
  const dd = dateStr.substring(4, 6);
  const fullYear = `20${yy}`;

  // Parse strike: divide by 1000
  const strikeNum = parseInt(strikeStr, 10) / 1000;

  return {
    underlying: root,
    expirationDate: `${fullYear}-${mm}-${dd}`,
    strike: formatOptionStrike(strikeNum),
    type: typeChar as 'C' | 'P'
  };
}

/**
 * Parse standardized option symbol format: "SYMBOL YYYY-MM-DD STRIKE C/P"
 * Example: "IBIT 2025-12-19 50.00 P"
 */
function parseStandardizedOption(symbol: string): OptionSymbolParts | null {
  // Pattern: SYMBOL YYYY-MM-DD STRIKE C/P
  // Symbol can include letters, numbers, dots (e.g., BRK.B)
  const pattern = /^([A-Z][A-Z0-9.]*)\s+(\d{4}-\d{2}-\d{2})\s+([\d.]+)\s+([CP])$/i;
  const match = symbol.trim().toUpperCase().match(pattern);

  if (match) {
    // Every group is required by the pattern, so these are always present.
    const [, underlying, expirationDate, strike, type] = match;
    if (!underlying || !expirationDate || !strike || !type) return null;
    return {
      underlying,
      expirationDate,
      strike,
      type: type as 'C' | 'P'
    };
  }

  return null;
}

/**
 * Parse CSV description format: "CALL/PUT COMPANY NAME $STRIKE EXP MM/DD/YY"
 * Example: "PUT MARA HOLDINGS INC $27 EXP 01/16/26"
 *
 * Note: This requires looking up the company name to get the ticker symbol.
 * Returns null if company name can't be resolved to a ticker.
 */
function parseCSVDescriptionOption(description: string): OptionSymbolParts | null {
  if (!description) return null;

  // Pattern: (CALL|PUT) COMPANY NAME $STRIKE EXP MM/DD/YY
  // Handle optional extra spaces before "EX" or "EXP"
  const pattern = /^(CALL|PUT)\s+(.+?)\s+\$?([\d.]+)\s+(?:EXP?|EX)\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})/i;
  const match = description.trim().toUpperCase().match(pattern);

  if (!match) return null;
  // Every group is required by the pattern, so these are always present.
  const [, type, name, strike, month, day, rawYear] = match;
  if (!type || !name || !strike || !month || !day || !rawYear) return null;

  const optionType = type as 'CALL' | 'PUT';
  const companyName = name.trim();
  const mm = month.padStart(2, '0');
  const dd = day.padStart(2, '0');
  let year = rawYear;

  // Convert 2-digit year to 4-digit (assume 20xx)
  if (year.length === 2) {
    year = `20${year}`;
  }

  // Look up ticker from company name
  const ticker = getCompanyTicker(companyName);
  if (!ticker) {
    // Can't resolve company name to ticker
    return null;
  }

  return {
    underlying: ticker,
    expirationDate: `${year}-${mm}-${dd}`,
    strike: strike,
    type: optionType === 'CALL' ? 'C' : 'P'
  };
}

// LAT-05: parsing is a pure function of the input string and the company
// name map (description format only), and it runs inside per-row and
// pairwise loops (lot replay, import/sync matching, the short-option book).
// Memoize per string; drop everything when the map changes.
const MEMO_CAP = 10_000;
const parseMemo = createBoundedMemo<Readonly<OptionSymbolParts> | null>(MEMO_CAP);
const standardizeMemo = createBoundedMemo<string | null>(MEMO_CAP);
let memoMappingVersion = getCompanyMappingVersion();

function syncMemoWithCompanyMap(): void {
  const version = getCompanyMappingVersion();
  if (version === memoMappingVersion) return;
  memoMappingVersion = version;
  resetOptionSymbolMemo();
}

/** Forget every memoized parse (tests). */
export function resetOptionSymbolMemo(): void {
  parseMemo.clear();
  standardizeMemo.clear();
}

/**
 * Parse any option symbol format and extract components. Memoized; each call
 * returns its own copy, so a caller may modify it.
 */
export function parseOptionSymbol(symbol: string): OptionSymbolParts | null {
  if (!symbol) return null;
  syncMemoWithCompanyMap();
  const parts = parseMemo.get(symbol, parseOptionSymbolUncached);
  return parts ? { ...parts } : null;
}

function parseOptionSymbolUncached(symbol: string): OptionSymbolParts | null {

  // Try standardized format first (most common after normalization)
  let parts = parseStandardizedOption(symbol);
  if (parts) return parts;

  // Try OCC format (e.g., "QQQ260106P00615000")
  parts = parseOCCOption(symbol);
  if (parts) return parts;

  // Try Schwab format
  parts = parseSchwabOption(symbol);
  if (parts) return parts;

  // Try Vanguard format
  parts = parseVanguardOption(symbol);
  if (parts) return parts;

  // Try Vanguard dot-separated format (e.g. "AMD.260618.C.240.00")
  parts = parseDottedVanguardOption(symbol);
  if (parts) return parts;

  // Try CSV description format (e.g., "PUT MARA HOLDINGS $27 EXP 01/16/26")
  parts = parseCSVDescriptionOption(symbol);
  if (parts) return parts;

  return null;
}

/**
 * Parse option symbol OR description and return OCC format symbol for storage.
 * This is the main entry point for normalizing option symbols during import.
 *
 * @param symbolOrDescription - Symbol or description to parse
 * @returns OCC format symbol or null if not an option
 */
export function parseAndStandardizeOption(symbolOrDescription: string): string | null {
  const parts = parseOptionSymbol(symbolOrDescription);
  if (parts) {
    return formatOCCOptionSymbol(parts);
  }
  return null;
}

/**
 * Format option symbol parts into human-readable display format.
 * Format: "SYMBOL YYYY-MM-DD STRIKE C/P"
 * Use this for UI display, NOT for storage.
 *
 * @example formatDisplayOptionSymbol(parts) => "AVXL 2025-09-19 9.00 P"
 */
export function formatDisplayOptionSymbol(parts: OptionSymbolParts): string {
  const normalizedStrike = formatOptionStrike(parseFloat(parts.strike));
  return `${parts.underlying} ${parts.expirationDate} ${normalizedStrike} ${parts.type}`;
}

/**
 * @deprecated Use formatDisplayOptionSymbol() for display, or formatOCCOptionSymbol() for storage
 * Kept for backwards compatibility - returns display format
 */
export function formatStandardOptionSymbol(parts: OptionSymbolParts): string {
  return formatDisplayOptionSymbol(parts);
}

/**
 * Convert option symbol parts to OCC (Options Clearing Corporation) format
 * OCC format: ROOTSYMBOL + YYMMDD + C/P + STRIKE*1000 padded to 8 digits
 * Example: "IBIT 2025-12-19 50.00 P" -> "IBIT251219P00050000"
 */
export function formatOCCOptionSymbol(parts: OptionSymbolParts): string {
  // Extract YYMMDD from YYYY-MM-DD
  // split() always returns at least one element, so `year` is always present.
  const [year = '', month, day] = parts.expirationDate.split('-');
  const yy = year.slice(2); // Last 2 digits of year
  const dateStr = `${yy}${month}${day}`;
  
  // Convert strike to integer (multiply by 1000) and pad to 8 digits
  const strikeNum = Math.round(parseFloat(parts.strike) * 1000);
  const strikeStr = strikeNum.toString().padStart(8, '0');
  
  return `${parts.underlying}${dateStr}${parts.type}${strikeStr}`;
}

/**
 * Convert any option symbol to OCC format
 * Returns null if not an option symbol
 */
export function toOCCSymbol(symbol: string): string | null {
  const parts = parseOptionSymbol(symbol);
  if (!parts) return null;
  return formatOCCOptionSymbol(parts);
}

/**
 * Convert an option symbol (any format) to human-readable display format.
 * Use this for displaying option symbols in the UI.
 *
 * @example getOptionDisplaySymbol("QQQ260106P00615000") => "QQQ 2026-01-06 615.00 P"
 * @example getOptionDisplaySymbol("AAPL") => "AAPL" (not an option, returned as-is)
 */
export function getOptionDisplaySymbol(symbol: string | null): string | null {
  if (!symbol) return null;

  const parts = parseOptionSymbol(symbol);
  if (parts) {
    return formatDisplayOptionSymbol(parts);
  }

  // Not an option symbol, return as-is
  return symbol;
}

/**
 * Standardize an option symbol to OCC format for storage.
 * Returns the OCC format symbol if it's an option, or the original symbol if not.
 *
 * @example standardizeOptionSymbol("AVXL 09/19/2025 9.00 P") => "AVXL250919P00009000"
 * @example standardizeOptionSymbol("AAPL") => "AAPL" (not an option, returned as-is)
 */
export function standardizeOptionSymbol(symbol: string | null): string | null {
  if (!symbol) return null;
  syncMemoWithCompanyMap();
  return standardizeMemo.get(symbol, (key) => {
    const parts = parseMemo.get(key, parseOptionSymbolUncached);
    // Not an option symbol: returned as-is
    return parts ? formatOCCOptionSymbol(parts) : key;
  });
}

/**
 * Check if a given date is in Eastern Daylight Time (EDT).
 * EDT is in effect from the second Sunday of March to the first Sunday of November.
 *
 * @param date - The date to check
 * @returns true if EDT is in effect, false if EST is in effect
 */
function isEasternDaylightTime(date: Date): boolean {
  // Use Intl to get the timezone abbreviation for America/New_York
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value === 'EDT';
}

/**
 * Calculate the removal time for an expired option
 * Options expire at market close (4:00 PM ET) on expiration date
 * They should be removed by market open (9:30 AM ET) the next trading day
 *
 * @param expirationDate - Date object or YYYY-MM-DD string
 * @returns Date representing when the option should be considered expired/removed
 */
export function getOptionRemovalTime(expirationDate: Date | string): Date {
  let expDate: Date;

  if (typeof expirationDate === 'string') {
    const [year, month, day] = expirationDate.split('-').map(Number);
    // A malformed string still yields an Invalid Date, as before.
    expDate = new Date(Date.UTC(year ?? NaN, (month ?? NaN) - 1, day));
  } else {
    expDate = new Date(expirationDate);
  }

  // Set to next day at 9:30 AM ET
  const removalTime = new Date(expDate);
  removalTime.setUTCDate(removalTime.getUTCDate() + 1);

  // 9:30 AM ET = 13:30 UTC during EDT (summer), 14:30 UTC during EST (winter)
  const utcHour = isEasternDaylightTime(removalTime) ? 13 : 14;
  removalTime.setUTCHours(utcHour, 30, 0, 0);

  return removalTime;
}

/**
 * Check if an option has expired based on its expiration date
 * Uses the 9:30 AM ET next-day rule for consistency across the application
 * 
 * @param symbol - The option symbol to check
 * @param referenceTime - Optional time to check against (defaults to now)
 * @returns true if the option is expired and should be removed/marked as expired
 */
export function isOptionExpiredByDate(symbol: string, referenceTime?: Date): boolean {
  const parts = parseOptionSymbol(symbol);
  if (!parts) {
    return false; // Not an option or couldn't parse
  }
  
  const now = referenceTime || new Date();
  const removalTime = getOptionRemovalTime(parts.expirationDate);
  
  return now >= removalTime;
}

/**
 * Compare two symbols for equality, accounting for different option formats.
 * Both symbols are converted to OCC format before comparison.
 *
 * @example areSymbolsEquivalent("QQQ260106P00615000", "QQQ 01/06/2026 615.00 P") => true
 * @example areSymbolsEquivalent("AAPL", "AAPL") => true
 * @example areSymbolsEquivalent(null, null) => true (both have no symbol)
 */
export function areSymbolsEquivalent(symbol1: string | null | undefined, symbol2: string | null | undefined): boolean {
  // Normalize empty strings to null for consistent comparison
  const s1 = symbol1?.trim() || null;
  const s2 = symbol2?.trim() || null;

  // If both are null/empty, they ARE equivalent (both have no symbol)
  // This is important for cash-only transactions like account interest
  if (!s1 && !s2) return true;
  // If one is null but not the other, they are not equivalent
  if (!s1 || !s2) return false;
  // If exactly the same, they are equivalent (fast path)
  if (s1.toUpperCase() === s2.toUpperCase()) return true;

  // Convert both to OCC format and compare
  // standardizeOptionSymbol returns OCC format for options, original for non-options
  const occ1 = standardizeOptionSymbol(s1);
  const occ2 = standardizeOptionSymbol(s2);

  return occ1 === occ2;
}
